<template>
  <page-header-wrapper>
    <a-space direction="vertical" style="width: 100%">
      <a-row :gutter="24">
        <a-col :span="12">
          <a-card :loading="loading" :title="$t('monitor.server.cpu')" :bordered="false">
            <a-row :gutter="24" v-if="server.cpu">
              <a-col :span="12">
                <a-space direction="vertical" size="large">
                  <a-statistic
                    :title="$t('monitor.server.cpuNum')"
                    :value="server.cpu.cpuNum"
                    :value-style="{ color: '#3f8600' }"
                    style="margin-right: 50px"
                  >
                    <template #prefix>
                      <a-icon type="setting" />
                    </template>
                  </a-statistic>
                  <a-statistic
                    :title="$t('monitor.server.cpuUsed')"
                    :value="server.cpu.used"
                    :precision="2"
                    suffix="%"
                    :value-style="{ color: '#3f8600' }"
                    style="margin-right: 50px"
                  >
                    <template #prefix>
                      <a-icon type="team" />
                    </template>
                  </a-statistic>
                </a-space>
              </a-col>
              <a-col :span="12">
                <a-space direction="vertical" size="large">
                  <a-statistic
                    :title="$t('monitor.server.cpuSys')"
                    :value="server.cpu.sys"
                    :precision="2"
                    suffix="%"
                    :value-style="{ color: '#3f8600' }"
                    style="margin-right: 50px"
                  >
                    <template #prefix>
                      <a-icon type="cloud-server" />
                    </template>
                  </a-statistic>
                  <a-statistic
                    :title="$t('monitor.server.cpuFree')"
                    :value="server.cpu.free"
                    :precision="2"
                    suffix="%"
                    :value-style="{ color: '#3f8600' }"
                    style="margin-right: 50px"
                  >
                    <template #prefix>
                      <a-icon type="inbox" />
                    </template>
                  </a-statistic>
                </a-space>
              </a-col>
            </a-row>
          </a-card>
        </a-col>
        <a-col :span="12">
          <a-card :loading="loading" :title="$t('monitor.server.memory')" :bordered="false">
            <a-table
              :loading="loading"
              size="small"
              rowKey="rowKey"
              :columns="memColumns"
              :data-source="memData"
              :pagination="false">
              <span slot="mem" slot-scope="text, record">
                <div :style="{color: record.rowKey === 'usage' && text > 80 ? 'red' : ''}">
                  <a-icon type="warning" style="color:#FFCC00" v-if="record.rowKey === 'usage' && text > 80" />
                  {{ text }} &nbsp;
                  <code v-if="record.rowKey === 'usage'">%</code>
                  <code v-if="record.rowKey !== 'usage'">G</code>
                </div>
              </span>
              <span slot="jvm" slot-scope="text, record">
                <div :style="{color: record.rowKey === 'usage' && text > 80 ? 'red' : ''}">
                  <a-icon type="warning" style="color:#FFCC00" v-if="record.rowKey === 'usage' && text > 80" />
                  {{ text }} &nbsp;
                  <code v-if="record.rowKey === 'usage'">%</code>
                  <code v-if="record.rowKey !== 'usage'">M</code>
                </div>
              </span>
            </a-table>
          </a-card>
        </a-col>
      </a-row>
      <a-row :gutter="24">
        <a-col :span="24">
          <a-card :loading="loading" :title="$t('monitor.server.serverInfo')" :bordered="false">
            <a-descriptions :column="2" v-if="server.sys">
              <a-descriptions-item :label="$t('monitor.server.computerName')">
                {{ server.sys.computerName }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.osName')">
                {{ server.sys.osName }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.computerIp')">
                {{ server.sys.computerIp }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.osArch')">
                {{ server.sys.osArch }}
              </a-descriptions-item>
            </a-descriptions>
          </a-card>
        </a-col>
      </a-row>
      <a-row :gutter="24">
        <a-col :span="24">
          <a-card :loading="loading" :title="$t('monitor.server.jvmInfo')" :bordered="false">
            <a-descriptions :column="2" v-if="server.jvm">
              <a-descriptions-item :label="$t('monitor.server.javaName')">
                {{ server.jvm.name }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.javaVersion')">
                {{ server.jvm.version }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.startTime')">
                {{ server.jvm.startTime }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.runTime')">
                {{ server.jvm.runTime }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.installPath')">
                {{ server.jvm.home }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.projectPath')">
                {{ server.sys.userDir }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('monitor.server.runArgs')">
                {{ server.jvm.inputArgs }}
              </a-descriptions-item>
            </a-descriptions>
          </a-card>
        </a-col>
      </a-row>
      <a-row :gutter="24">
        <a-col :span="24">
          <a-card :loading="loading" :title="$t('monitor.server.diskStatus')" :bordered="false">
            <a-table
              :loading="loading"
              :size="tableSize"
              rowKey="dirName"
              :columns="sysColumns"
              :data-source="sysData"
              :pagination="false"
              :bordered="tableBordered"
            >
              <span slot="total" slot-scope="text">
                {{ text }}
              </span>
              <span slot="free" slot-scope="text">
                {{ text }}
              </span>
              <span slot="used" slot-scope="text">
                {{ text }}
              </span>
              <span slot="usage" slot-scope="text">
                <div :style="{color: text > 80 ? 'red' : ''}">
                  <a-icon type="warning" style="color:#FFCC00" v-if="text > 80" />
                  {{ text }}<code>%</code>
                </div>
              </span>
            </a-table>
          </a-card>
        </a-col>
      </a-row>
    </a-space>
  </page-header-wrapper>
</template>

<script>

import { getServer } from '@/api/monitor/server'
import { tableMixin } from '@/store/table-mixin'

export default {
  name: 'Server',
  mixins: [tableMixin],
  data () {
    return {
      server: {},
      loading: true,
      memRows: [],
      sysData: []
    }
  },
  created () {
    this.getList()
  },
  computed: {
    memColumns () {
      return [
        {
          title: this.$t('monitor.server.attr'),
          dataIndex: 'name'
        },
        {
          title: this.$t('monitor.server.memColumn'),
          dataIndex: 'mem',
          scopedSlots: { customRender: 'mem' }
        },
        {
          title: 'JVM',
          dataIndex: 'jvm',
          scopedSlots: { customRender: 'jvm' }
        }
      ]
    },
    memData () {
      return this.memRows.map(row => ({
        ...row,
        name: this.$t('monitor.server.mem.' + row.rowKey)
      }))
    },
    sysColumns () {
      return [
        {
          title: this.$t('monitor.server.dirPath'),
          dataIndex: 'dirName'
        },
        {
          title: this.$t('monitor.server.fileSystem'),
          dataIndex: 'sysTypeName'
        },
        {
          title: this.$t('monitor.server.diskType'),
          dataIndex: 'typeName'
        },
        {
          title: this.$t('monitor.server.totalSize'),
          dataIndex: 'total',
          scopedSlots: { customRender: 'total' }
        },
        {
          title: this.$t('monitor.server.freeSize'),
          dataIndex: 'free',
          scopedSlots: { customRender: 'free' }
        },
        {
          title: this.$t('monitor.server.usedSize'),
          dataIndex: 'used',
          scopedSlots: { customRender: 'used' }
        },
        {
          title: this.$t('monitor.server.usagePercent'),
          dataIndex: 'usage',
          scopedSlots: { customRender: 'usage' }
        }
      ]
    }
  },
  methods: {
    /** 查询服务信息 */
    getList () {
      getServer().then(response => {
        const serverData = response.data
        this.server = serverData
        this.memRows = [
          {
            rowKey: 'total',
            mem: serverData.mem.total,
            jvm: serverData.jvm.total
          },
          {
            rowKey: 'used',
            mem: serverData.mem.used,
            jvm: serverData.jvm.used
          },
          {
            rowKey: 'free',
            mem: serverData.mem.free,
            jvm: serverData.jvm.free
          },
          {
            rowKey: 'usage',
            mem: serverData.mem.usage,
            jvm: serverData.jvm.usage
          }
        ]
        this.sysData = serverData.sysFiles
        setTimeout(() => {
          this.loading = false
        }, 500)
      })
    }
  }
}
</script>
